"use server"

import { sdk } from "@lib/config"
import { FetchError } from "@medusajs/js-sdk"
import { revalidateTag } from "next/cache"
import { cookies as nextCookies } from "next/headers"
import { getAuthHeaders, getCacheOptions, getCacheTag } from "./cookies"

export type FitmentMake = {
  make_id: number
  name: string
}

export type FitmentModel = {
  model_id: number
  name: string
}

export type FitmentVehicle = {
  year: number
  make: string
  model: string
}

export type ResolvedVehicle = {
  base_vehicle_id: number
  vehicle: FitmentVehicle
}

export type ActiveVehicle = FitmentVehicle & {
  base_vehicle_id: number
  engine?: string | null
}

export type GarageVehicle = {
  id: string
  base_vehicle_id: number
  nickname: string | null
  is_default: boolean
  vehicle: FitmentVehicle
}

export type SearchHit = {
  id: string
  title: string
  handle: string
  thumbnail: string | null
  sku: string | null
  brand: string | null
  category: string | null
  price_usd: number | null
  fitment_note: string | null
  fitment_engine: string | null
}

export type SearchFacets = {
  category?: Record<string, number>
  brand?: Record<string, number>
  part_type?: Record<string, number>
}

export type SearchResults = {
  hits: SearchHit[]
  total: number
  facets: SearchFacets
  limit?: number
  offset?: number
}

export type ProductFitment = {
  base_vehicle_id: number
  vehicle: FitmentVehicle
  qty: number | null
  position: string | null
  notes: string | null
  engine: string | null
  qualifier: string | null
}

export type ProductFitmentResult = {
  fitments: ProductFitment[]
  fits: boolean | null
}

export const listYears = async (): Promise<number[]> => {
  const next = {
    ...(await getCacheOptions("fitment")),
  }

  return sdk.client
    .fetch<{ years: number[] }>(`/store/fitment/years`, {
      method: "GET",
      next,
      cache: "force-cache",
    })
    .then(({ years }) => years)
    .catch(() => [])
}

export const listMakes = async (year: number): Promise<FitmentMake[]> => {
  const next = {
    ...(await getCacheOptions("fitment")),
  }

  return sdk.client
    .fetch<{ makes: FitmentMake[] }>(`/store/fitment/makes`, {
      method: "GET",
      query: { year },
      next,
      cache: "force-cache",
    })
    .then(({ makes }) => makes)
    .catch(() => [])
}

export const listModels = async (
  year: number,
  makeId: number
): Promise<FitmentModel[]> => {
  const next = {
    ...(await getCacheOptions("fitment")),
  }

  return sdk.client
    .fetch<{ models: FitmentModel[] }>(`/store/fitment/models`, {
      method: "GET",
      query: { year, make_id: makeId },
      next,
      cache: "force-cache",
    })
    .then(({ models }) => models)
    .catch(() => [])
}

// Resolves a year/make/model combination to a base vehicle. Returns null when
// the backend has no matching vehicle (404).
export const resolveVehicle = async (
  year: number,
  makeId: number,
  modelId: number
): Promise<ResolvedVehicle | null> => {
  const next = {
    ...(await getCacheOptions("fitment")),
  }

  return sdk.client
    .fetch<ResolvedVehicle>(`/store/fitment/resolve`, {
      method: "GET",
      query: { year, make_id: makeId, model_id: modelId },
      next,
      cache: "force-cache",
    })
    .catch(() => null)
}

// Engines available for a base vehicle. The backend returns plain engine
// strings ("5.7L 350ci V8"), already deduped and sorted. Empty array when the
// vehicle has no engine data.
export const listEngines = async (
  baseVehicleId: number
): Promise<string[]> => {
  const next = {
    ...(await getCacheOptions("fitment")),
  }

  return sdk.client
    .fetch<{ engines: string[] }>(`/store/fitment/engines`, {
      method: "GET",
      query: { base_vehicle_id: baseVehicleId },
      next,
      cache: "force-cache",
    })
    .then(({ engines }) => engines ?? [])
    .catch(() => [])
}

export type FitmentVehicleSummary = {
  base_vehicle_id: number
  year: number
  make: string
  model: string
  parts: number | string
  slug: string
}

export type FitmentVehicleList = {
  vehicles: FitmentVehicleSummary[]
  count: number
}

// Paged list of base vehicles that have at least one product, for the
// /fits application landing pages and the sitemap. Max limit is 1000.
// Pass withRadiators to narrow to vehicles that have at least one radiator
// (the /cooling-system bundle landing pages).
export const listFitmentVehicles = async (
  limit = 100,
  offset = 0,
  withRadiators = false
): Promise<FitmentVehicleList> => {
  const next = {
    ...(await getCacheOptions("fitment")),
  }

  return sdk.client
    .fetch<FitmentVehicleList>(`/store/fitment/vehicles`, {
      method: "GET",
      query: {
        limit,
        offset,
        ...(withRadiators ? { with: "radiators" } : {}),
      },
      next,
      cache: "force-cache",
    })
    .catch(() => ({ vehicles: [], count: 0 }))
}

// Exhaustive vehicle list for the /fits and /cooling-system index pages.
// The endpoint caps limit at 1000, so page through until count is reached
// (~5,100 vehicles today — a handful of cached requests).
export const listAllFitmentVehicles = async (
  withRadiators = false
): Promise<FitmentVehicleList> => {
  const pageSize = 1000
  const first = await listFitmentVehicles(pageSize, 0, withRadiators)
  const vehicles = [...first.vehicles]

  while (vehicles.length < first.count) {
    const { vehicles: page } = await listFitmentVehicles(
      pageSize,
      vehicles.length,
      withRadiators
    )
    if (!page.length) {
      break
    }
    vehicles.push(...page)
  }

  return { vehicles, count: first.count }
}

// Resolves a vehicle landing-page slug ("1969-chevrolet-camaro") to its base
// vehicle. Returns null when the backend has no matching vehicle (404).
export const getFitmentVehicleBySlug = async (
  slug: string
): Promise<FitmentVehicleSummary | null> => {
  const next = {
    ...(await getCacheOptions("fitment")),
  }

  return sdk.client
    .fetch<{ vehicle: FitmentVehicleSummary }>(`/store/fitment/vehicles`, {
      method: "GET",
      query: { slug },
      next,
      cache: "force-cache",
    })
    .then(({ vehicle }) => vehicle)
    .catch(() => null)
}

export const searchProducts = async ({
  q,
  baseVehicleId,
  engine,
  category,
  brand,
  priceMin,
  priceMax,
  limit = 12,
  offset = 0,
}: {
  q?: string
  baseVehicleId?: number
  engine?: string
  category?: string
  brand?: string
  priceMin?: number
  priceMax?: number
  limit?: number
  offset?: number
}): Promise<SearchResults> => {
  const next = {
    ...(await getCacheOptions("products")),
  }

  const query: Record<string, string | number> = { limit, offset }

  if (q) {
    query.q = q
  }

  if (baseVehicleId) {
    query.base_vehicle_id = baseVehicleId
  }

  if (engine) {
    query.engine = engine
  }

  if (category) {
    query.category = category
  }

  if (brand) {
    query.brand = brand
  }

  if (priceMin !== undefined) {
    query.price_min = priceMin
  }

  if (priceMax !== undefined) {
    query.price_max = priceMax
  }

  return sdk.client
    .fetch<SearchResults>(`/store/search`, {
      method: "GET",
      query,
      next,
      cache: "force-cache",
    })
    .catch(() => ({ hits: [], total: 0, facets: {} }))
}

export const getProductFitment = async (
  productId: string,
  baseVehicleId?: number
): Promise<ProductFitmentResult | null> => {
  const next = {
    ...(await getCacheOptions("fitment")),
  }

  return sdk.client
    .fetch<ProductFitmentResult>(`/store/products/${productId}/fitment`, {
      method: "GET",
      query: baseVehicleId ? { base_vehicle_id: baseVehicleId } : {},
      next,
      cache: "force-cache",
    })
    .catch(() => null)
}

export const getGarage = async (): Promise<GarageVehicle[]> => {
  const authHeaders = await getAuthHeaders()

  if (!("authorization" in authHeaders)) {
    return []
  }

  const next = {
    ...(await getCacheOptions("garage")),
  }

  return sdk.client
    .fetch<{ vehicles: GarageVehicle[] }>(`/store/customers/me/garage`, {
      method: "GET",
      headers: { ...authHeaders },
      next,
      cache: "force-cache",
    })
    .then(({ vehicles }) => vehicles)
    .catch(() => [])
}

export type GarageActionResult = {
  vehicle: GarageVehicle | null
  error: string | null
}

export const addGarageVehicle = async (
  baseVehicleId: number,
  nickname?: string,
  isDefault?: boolean
): Promise<GarageActionResult> => {
  const headers = {
    ...(await getAuthHeaders()),
  }

  try {
    const { vehicle } = await sdk.client.fetch<{ vehicle: GarageVehicle }>(
      `/store/customers/me/garage`,
      {
        method: "POST",
        headers,
        body: {
          base_vehicle_id: baseVehicleId,
          ...(nickname ? { nickname } : {}),
          ...(isDefault !== undefined ? { is_default: isDefault } : {}),
        },
      }
    )

    const cacheTag = await getCacheTag("garage")
    if (cacheTag) {
      revalidateTag(cacheTag)
    }

    return { vehicle, error: null }
  } catch (error) {
    const fetchError = error as FetchError

    if (fetchError.status === 409) {
      return { vehicle: null, error: "This vehicle is already in your garage." }
    }

    return { vehicle: null, error: "Could not save the vehicle. Try again." }
  }
}

export const removeGarageVehicle = async (
  id: string
): Promise<{ id: string; error: string | null }> => {
  const headers = {
    ...(await getAuthHeaders()),
  }

  try {
    await sdk.client.fetch<{ id: string; deleted: boolean }>(
      `/store/customers/me/garage/${id}`,
      {
        method: "DELETE",
        headers,
      }
    )

    const cacheTag = await getCacheTag("garage")
    if (cacheTag) {
      revalidateTag(cacheTag)
    }

    return { id, error: null }
  } catch {
    return { id, error: "Could not remove the vehicle. Try again." }
  }
}

// Active vehicle cookie. Intentionally not httpOnly so it only carries
// non-sensitive display data (year/make/model + base vehicle id).
const VEHICLE_COOKIE = "_tnp_vehicle"

export const getActiveVehicle = async (): Promise<ActiveVehicle | null> => {
  try {
    const cookies = await nextCookies()
    const value = cookies.get(VEHICLE_COOKIE)?.value

    if (!value) {
      return null
    }

    const parsed = JSON.parse(value) as ActiveVehicle

    if (!parsed?.base_vehicle_id) {
      return null
    }

    return parsed
  } catch {
    return null
  }
}

export const setActiveVehicle = async (vehicle: ActiveVehicle) => {
  const cookies = await nextCookies()
  cookies.set(VEHICLE_COOKIE, JSON.stringify(vehicle), {
    maxAge: 60 * 60 * 24 * 365,
    httpOnly: false,
    sameSite: "strict",
    secure: process.env.NODE_ENV === "production",
  })
}

// One-shot helper for vehicle-specific pages: stamp the page's vehicle as
// the active garage vehicle (the caller then navigates to /vehicle, which
// renders results for it immediately).
export const activateVehicleAndShop = async (vehicle: {
  base_vehicle_id: number
  year: number
  make: string
  model: string
}) => {
  await setActiveVehicle({
    base_vehicle_id: vehicle.base_vehicle_id,
    year: vehicle.year,
    make: vehicle.make,
    model: vehicle.model,
    engine: null,
  })
}

export const clearActiveVehicle = async () => {
  const cookies = await nextCookies()
  cookies.set(VEHICLE_COOKIE, "", {
    maxAge: -1,
  })
}
